// src/App.tsx
import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom'
import LanguagePicker from './components/LanguagePicker'
import SmokeTrail from './components/SmokeTrail'
import Footer from './components/Footer'

import HomePage from './pages/HomePage'
import MusicPage from './pages/MusicPage'
import PhotosPage from './pages/PhotosPage'
import VideosPage from './pages/VideosPage'
import FanWallPage from './pages/FanWallPage'
import MerchPage from './pages/MerchPage'
import ShopPage from './pages/ShopPage'
import ProductPage from './pages/ProductPage'
import ContactPage from './pages/ContactPage'
import DownloadPage from './pages/DownloadPage'
import Navbar from './components/Navbar'
import ProtectedRoute from './components/ProtectedRoute'

import { AuthProvider } from './context/AuthContext'
import { LangContext } from './context/LangContext'
import type { Lang } from './context/LangContext'
import { i18n } from './i18n'
import {
  AdminLoginPage,
  DashboardPage,
  MusicManagerPage,
  AlbumEditorPage,
  StandaloneTrackEditorPage,
  PhotosManagerPage,
  PhotoAlbumEditorPage,
  VideosManagerPage,
  FanWallManagerPage,
  SocialLinksManagerPage,
  MerchManagerPage,
  SettingsPage,
} from './admin'
import AdminLayout from './layouts/AdminLayout'

export default function App() {
  const [lang, setLang] = useState<Lang>(() => {
    return (localStorage.getItem('appLang') as Lang) || 'en'
  })

  useEffect(() => {
    localStorage.setItem('appLang', lang)
  }, [lang])

  const setLangAndI18n = (l: Lang) => {
    i18n.changeLanguage(l)
    setLang(l)
  }

  return (
    <Router>
      <AuthProvider>
        <LangContext.Provider value={{ lang, setLang: setLangAndI18n }}>
          <AppBody lang={lang} setLangAndI18n={setLangAndI18n} />
        </LangContext.Provider>
      </AuthProvider>
    </Router>
  )
}

function AppBody({ lang, setLangAndI18n }: { lang: Lang; setLangAndI18n: (l: Lang) => void }) {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')

  return (
    <>
      <SmokeTrail />
      <div className={`relative flex min-h-screen flex-col ${isAdminRoute ? 'admin-root' : ''}`}>
        {!isAdminRoute && (
          <div className="relative">
            <Navbar />
            <header className="absolute right-4 top-1/2 z-10 -translate-y-1/2">
              <LanguagePicker currentLang={lang} onChange={setLangAndI18n} />
            </header>
          </div>
        )}

        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/music" element={<MusicPage />} />
            <Route path="/photos" element={<PhotosPage />} />
            <Route path="/photos/:id" element={<PhotosPage />} />
            <Route path="/videos" element={<VideosPage />} />
            <Route path="/fanwall" element={<FanWallPage />} />
            <Route path="/merch" element={<MerchPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/download" element={<DownloadPage />} />

            <Route path="/login" element={<Navigate to="/admin/login" replace />} />

            <Route path="/admin" element={<Outlet />}>
              <Route path="login" element={<AdminLoginPage />} />
              <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="music" element={<MusicManagerPage />} />
                <Route path="music/new" element={<AlbumEditorPage />} />
                <Route path="music/albums/:id" element={<AlbumEditorPage />} />
                <Route path="music/tracks/:trackId" element={<StandaloneTrackEditorPage />} />
                <Route path="photos" element={<PhotosManagerPage />} />
                <Route path="photos/new" element={<PhotoAlbumEditorPage />} />
                <Route path="photos/albums/:id" element={<PhotoAlbumEditorPage />} />
                <Route path="videos" element={<VideosManagerPage />} />
                <Route path="fanwall" element={<FanWallManagerPage />} />
                <Route path="social" element={<SocialLinksManagerPage />} />
                <Route path="merch" element={<MerchManagerPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>
        </Routes>

        {!isAdminRoute && <Footer />}
      </div>
    </>
  )
}
